import { memo, useCallback, useMemo, useState } from 'react';

import { ChevronDownIcon, CircleCheckIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { MultiSelectField, type MultiSelectOption } from '@/components/atoms/MultiSelect';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { RichRadioGroup, RichRadioItem } from '@/components/ui/rich-radio';
import { useAccount } from '@/hooks/useAccount';
import useHomeCheckList, { AccountUserPrefsEnum } from '@/hooks/useHomeCheckList';
import { useInvitations } from '@/hooks/useInvitations';
import useMembers from '@/hooks/useMembers';
import { useRoles } from '@/hooks/useRoles';
import { useTeams } from '@/hooks/useTeams';
import { cn } from '@/lib/utils';
import { popModal } from '@/redux/modals/actions';
import { RoleType } from '@/shared/types/member';
import { roleSelectOptions } from '@/util/roleOptions';
import { emailPattern } from '@/util/validation/patterns';

type InviteRow = { email: string; roleIds: string[]; teamIds: string[] };

type FormType = { joinType: 'role' | 'team'; rows: InviteRow[] };

type SentInvitation = { email: string; roleLabel: string };

const parseRowEmails = (email: string): string[] =>
  email
    .split(',')
    .map((e) => e.trim())
    .filter(Boolean);

const ModalMemberInvite = () => {
  const { t } = useTranslation();
  const { accessTeams: teams } = useTeams();
  const { markAsComplete } = useHomeCheckList();
  const { accountInvitations, createInvitationAsync } = useInvitations();
  const { members } = useMembers();
  const { roles, availableRoles, listStatus } = useRoles();
  const { account } = useAccount();
  const dispatch = useDispatch();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [transition, setTransition] = useState<'initial' | 'forward' | 'backward'>('initial');
  const [sentInvitations, setSentInvitations] = useState<SentInvitation[]>([]);

  const goToStep = useCallback(
    (next: 1 | 2 | 3) => {
      setTransition(next >= step ? 'forward' : 'backward');
      setStep(next);
    },
    [step]
  );

  const stepTransitionClass = (() => {
    const base = 'duration-300 motion-reduce:animate-none motion-reduce:transform-none';
    if (transition === 'initial') {
      return cn(base, 'animate-in fade-in-0');
    }
    if (transition === 'backward') {
      return cn(base, 'animate-in fade-in-0 slide-in-from-left-6');
    }
    return cn(base, 'animate-in fade-in-0 slide-in-from-right-6');
  })();

  const form = useForm<FormType>({
    mode: 'onSubmit',
    defaultValues: { joinType: 'role', rows: [{ email: '', roleIds: [], teamIds: [] }] },
  });
  const { control, handleSubmit, getValues, setFocus } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'rows' });

  const rows = useWatch({ control, name: 'rows' });
  const joinType = useWatch({ control, name: 'joinType' });

  const teamOptions: MultiSelectOption[] = useMemo(
    () =>
      teams.map((team) => ({
        label: team.name,
        value: team.team_id,
        description: team.description,
      })),
    [teams]
  );

  const roleOptions: MultiSelectOption[] = useMemo(
    () =>
      roleSelectOptions(availableRoles, roles, {
        system: t('roles.system_roles'),
        custom: t('roles.custom_roles'),
      }),
    [availableRoles, roles, t]
  );

  const inviteCount = (rows ?? []).reduce((acc, row) => acc + parseRowEmails(row.email).length, 0);

  const sendButtonLabel = isLoading
    ? t('invitations.sending')
    : inviteCount > 1
      ? t('invitations.send_invite_count', { 0: inviteCount })
      : t('invitations.send_invite');

  const handleClose = useCallback(() => dispatch(popModal()), [dispatch]);

  const onSubmit = useCallback(
    async (data: FormType) => {
      setIsLoading(true);
      try {
        const invitations = data.rows.flatMap((row) => {
          const emails = parseRowEmails(row.email);
          if (emails.length === 0) {
            return [];
          }

          const roleLabel =
            data.joinType === 'role'
              ? row.roleIds
                  .map((id) => availableRoles.find((r) => r.value === id)?.label)
                  .filter((l): l is string => !!l)
                  .join(', ')
              : row.teamIds
                  .map((id) => teamOptions.find((opt) => opt.value === id)?.label)
                  .filter((l): l is string => !!l)
                  .join(', ');

          return emails.map((email) => ({
            email: email.toLowerCase(),
            roleLabel,
            payload: {
              role: RoleType.RBAC,
              email: email.toLowerCase(),
              role_ids: data.joinType === 'role' ? row.roleIds : [],
              team_ids: data.joinType === 'team' ? row.teamIds : [],
            },
          }));
        });

        await Promise.all(invitations.map(({ payload }) => createInvitationAsync(payload)));

        markAsComplete(AccountUserPrefsEnum.ADD_TEAM_MEMBERS);
        setSentInvitations(invitations.map(({ email, roleLabel }) => ({ email, roleLabel })));
        goToStep(3);
      } catch {
        // createInvitationAsync surfaces its own error toast
      } finally {
        setIsLoading(false);
      }
    },
    [createInvitationAsync, markAsComplete, availableRoles, teamOptions, goToStep]
  );

  const emailRules = useMemo(
    () => ({
      validate: {
        emailFormat: (value: string) => {
          if (!value?.trim()) {
            return true;
          }
          const emails = parseRowEmails(value);
          const invalid = emails.filter((e) => !emailPattern.test(e));
          return invalid.length === 0 || `${t('errors.invalid_email')}: ${invalid.join(', ')}`;
        },
        alreadyInvited: (value: string) => {
          if (!value?.trim()) {
            return true;
          }
          const emails = parseRowEmails(value);
          const existing = emails.find((e) =>
            accountInvitations?.some((inv) => inv.email === e.toLowerCase())
          );
          return !existing || t('invitations.already_invited', { 0: existing });
        },
        alreadyMember: (value: string) => {
          if (!value?.trim()) {
            return true;
          }
          const emails = parseRowEmails(value);
          const existing = emails.find((e) => members?.some((m) => m.email === e.toLowerCase()));
          return !existing || t('invitations.already_member', { 0: existing });
        },
      },
    }),
    [accountInvitations, members, t]
  );

  const getSelectionRules = useCallback(
    (index: number) => ({
      validate: {
        required: (value: string[]) => {
          const emailValue = getValues(`rows.${index}.email`);
          if (!emailValue?.trim()) {
            return true;
          }
          const message =
            joinType === 'role' ? t('invitations.select_a_role') : t('invitations.select_a_team');
          return value.length > 0 || message;
        },
      },
    }),
    [getValues, joinType, t]
  );

  const handleSendInvite = useCallback(() => {
    if (inviteCount === 0) {
      setFocus('rows.0.email');
      return;
    }

    const emptyIndices = (rows ?? [])
      .map((row, index) => ({ index, empty: parseRowEmails(row.email).length === 0 }))
      .filter(({ empty }) => empty)
      .map(({ index }) => index);

    if (emptyIndices.length > 0) {
      remove(emptyIndices);
    }

    handleSubmit(onSubmit)();
  }, [inviteCount, setFocus, rows, remove, handleSubmit, onSubmit]);

  const dialogTitle =
    step === 3 ? (
      t('invitations.invitations_sent')
    ) : (
      <>
        {t('invitations.invite_members')}
        <span className="ml-1 text-informational">{account.name}</span>
      </>
    );

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent
        className="flex min-h-112 flex-col gap-0 p-0 sm:max-w-2xl"
        aria-describedby={undefined}
        onInteractOutside={(e) => {
          if (step === 2) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-xl leading-7 font-semibold">{dialogTitle}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            autoComplete="off"
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={(e) => {
              e.preventDefault();
              if (step === 1) {
                goToStep(2);
              } else if (step === 2) {
                handleSendInvite();
              }
            }}
          >
            <div key={step} className={cn('flex flex-1 flex-col px-6 pb-4', stepTransitionClass)}>
              {step === 1 && (
                <div className="flex flex-col gap-3">
                  <p className="px-0.5 text-lg leading-7 font-medium">
                    {t('invitations.how_should_members_join')}
                  </p>
                  <FormField
                    control={control}
                    name="joinType"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <RichRadioGroup
                            value={field.value}
                            onValueChange={field.onChange}
                            className="grid-cols-1 gap-2"
                          >
                            <RichRadioItem
                              value="role"
                              title={t('invitations.by_role')}
                              description={t('invitations.by_role_description')}
                              className="w-full max-w-full"
                            />
                            <RichRadioItem
                              value="team"
                              title={t('invitations.by_team')}
                              description={t('invitations.by_team_description')}
                              className="w-full max-w-full"
                            />
                          </RichRadioGroup>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {step === 2 && (
                <div className="flex flex-col gap-2">
                  <div className="flex gap-3">
                    <span className="min-w-0 flex-1 text-sm font-medium">
                      {t('invitations.email')}
                    </span>
                    <span className="w-56 shrink-0 text-sm font-medium">
                      {joinType === 'role' ? t('common.members_roles') : t('common.teams')}
                    </span>
                  </div>
                  {fields.map((field, index) => (
                    <div key={field.id} className="flex items-start gap-3">
                      <FormField
                        control={control}
                        name={`rows.${index}.email`}
                        rules={emailRules}
                        render={({ field: emailField, fieldState }) => (
                          <FormItem className="min-w-0 flex-1">
                            <FormControl>
                              <InputGroup className={fieldState.error ? 'border-destructive' : ''}>
                                <InputGroupInput
                                  {...emailField}
                                  placeholder={t('invitations.email_example_placeholder')}
                                  autoFocus={index === 0}
                                />
                                {fields.length > 1 && (
                                  <InputGroupAddon align="inline-end">
                                    <Button
                                      className="hover:text-destructive hover:[&_svg]:text-destructive"
                                      size="icon-sm"
                                      variant="ghost-neutral"
                                      type="button"
                                      onClick={() => remove(index)}
                                      aria-label={t('common.remove')}
                                    >
                                      <Trash2Icon />
                                    </Button>
                                  </InputGroupAddon>
                                )}
                              </InputGroup>
                            </FormControl>
                            {fieldState.error && (
                              <FormMessage>{fieldState.error.message}</FormMessage>
                            )}
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={control}
                        name={
                          joinType === 'role' ? `rows.${index}.roleIds` : `rows.${index}.teamIds`
                        }
                        rules={getSelectionRules(index)}
                        render={({ field: selField, fieldState }) => {
                          const options = joinType === 'role' ? roleOptions : teamOptions;
                          const selectedLabels = options
                            .filter((o) => selField.value.includes(o.value))
                            .map((o) => o.label)
                            .join(', ');
                          const isDisabled = joinType === 'role' && listStatus === 'pending';
                          return (
                            <FormItem className="shrink-0">
                              <FormControl>
                                <MultiSelectField
                                  options={options}
                                  value={selField.value}
                                  onChange={selField.onChange}
                                  disabled={isDisabled}
                                  error={!!fieldState.error}
                                  placeholder={
                                    joinType === 'role'
                                      ? t('invitations.assign_roles')
                                      : t('invitations.assign_teams')
                                  }
                                  sortOptions={joinType !== 'role'}
                                  popoverClassName={joinType === 'role' ? 'w-96' : 'w-80'}
                                  popoverAlign="end"
                                  trigger={
                                    <Button
                                      variant="outline-neutral"
                                      type="button"
                                      disabled={isDisabled}
                                      className={cn(
                                        'h-10 w-56 justify-between px-3 font-normal',
                                        fieldState.error && 'border-destructive'
                                      )}
                                    >
                                      <span
                                        title={selectedLabels || undefined}
                                        className={cn(
                                          'min-w-0 truncate',
                                          !selectedLabels && 'text-foreground-muted'
                                        )}
                                      >
                                        {selectedLabels || `${t('common.select')}...`}
                                      </span>
                                      <ChevronDownIcon size={16} className="shrink-0" />
                                    </Button>
                                  }
                                />
                              </FormControl>
                              {fieldState.error && (
                                <FormMessage>{fieldState.error.message}</FormMessage>
                              )}
                            </FormItem>
                          );
                        }}
                      />
                    </div>
                  ))}
                  {fields.length === 1 && (
                    <FormDescription>{t('invitations.separate_multiple')}</FormDescription>
                  )}
                  {fields.length < 5 && (
                    <Button
                      variant="ghost-neutral"
                      type="button"
                      size="sm"
                      onClick={() => append({ email: '', roleIds: [], teamIds: [] })}
                      className="flex items-center gap-1.5 self-start"
                    >
                      <PlusIcon size={14} />
                      {t('invitations.add_another')}
                    </Button>
                  )}
                </div>
              )}

              {step === 3 && (
                <div>
                  <div className="mb-1 flex justify-between border-b pb-3">
                    <span className="text-sm font-medium text-foreground-muted">
                      {t('invitations.email')}
                    </span>
                    <span className="text-sm font-medium text-foreground-muted">
                      {joinType === 'role' ? t('common.members_roles') : t('common.teams')}
                    </span>
                  </div>
                  {sentInvitations.map((inv, index) => (
                    <div
                      key={inv.email}
                      className={cn(
                        'flex items-start justify-between py-3',
                        index < sentInvitations.length - 1 && 'border-b'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{inv.email}</span>
                        <CircleCheckIcon size={16} className="shrink-0 text-successful" />
                      </div>
                      <span className="text-sm text-foreground-muted">{inv.roleLabel}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter className="px-6 pt-4 pb-6">
              {step === 1 && (
                <>
                  <Button variant="ghost-neutral" type="button" onClick={handleClose}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    autoFocus
                    className="focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    {t('common.continue')}
                  </Button>
                </>
              )}
              {step === 2 && (
                <>
                  <Button variant="ghost-neutral" type="button" onClick={handleClose}>
                    {t('common.cancel')}
                  </Button>
                  <Button variant="outline-neutral" type="button" onClick={() => goToStep(1)}>
                    {t('common.back')}
                  </Button>
                  <Button type="submit" isLoading={isLoading}>
                    {sendButtonLabel}
                  </Button>
                </>
              )}
              {step === 3 && (
                <Button type="button" onClick={handleClose}>
                  {t('common.done')}
                </Button>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default memo(ModalMemberInvite);
